import { type RefObject, useLayoutEffect } from 'react';

/** Position the open panel within the viewport using the owning nav’s alignment rule. */
export const useGlobalNavDropdownPosition = (
	panel: HTMLDivElement | null,
	alignToGlobalMenu = false,
	ownerRef?: RefObject<HTMLUListElement>,
) => {
	useLayoutEffect(() => {
		const trigger = panel?.parentElement?.querySelector('a');
		const menubar = panel?.closest('ul');
		if (!panel || !trigger || !menubar) return;
		// Compound nav rows share the active panel; only its owning row may position it.
		if (ownerRef && menubar !== ownerRef.current) return;

		const updatePosition = () => {
			const alignmentTrigger = alignToGlobalMenu
				? (menubar.querySelector('a') ?? trigger)
				: trigger;
			const textLeft =
				alignmentTrigger.getBoundingClientRect().left +
				(parseFloat(getComputedStyle(alignmentTrigger).paddingLeft) || 0);
			const viewportWidth = document.documentElement.clientWidth;
			const panelWidth = panel.getBoundingClientRect().width;
			const preferredLeft =
				alignToGlobalMenu && viewportWidth <= 1440 ? (viewportWidth - panelWidth) / 2 : textLeft;
			const viewportLeft = Math.max(32, Math.min(preferredLeft, viewportWidth - 32 - panelWidth));
			const containingBlock = panel.offsetParent ?? menubar;
			// Keep the measured position on this panel when it closes. Clearing it
			// while opacity is transitioning makes the outgoing panel jump left.
			panel.style.left = `${viewportLeft - containingBlock.getBoundingClientRect().left}px`;
		};

		updatePosition();
		const observer = new ResizeObserver(updatePosition);
		observer.observe(panel);
		observer.observe(menubar);
		const nav = menubar.closest('nav');
		if (nav) observer.observe(nav);
		window.addEventListener('resize', updatePosition);
		return () => {
			observer.disconnect();
			window.removeEventListener('resize', updatePosition);
		};
	}, [panel, alignToGlobalMenu, ownerRef]);
};
